<div class="container">
  <header class="header">
    <button class="back-button" (click)="goBack()">
      <span class="back-arrow">←</span>
    </button>
    <h1 class="header-text">Privacy Policy</h1>
  </header>

  <div class="content">
    <p class="last-updated">Last Updated: {{ lastUpdated }}</p>

    <section class="section">
      <h2 class="heading">1. Introduction</h2>
      <p class="paragraph">
        Welcome to DropAddy ("we," "our," or "us"). We are committed to protecting your privacy and ensuring you have a positive experience on our dating platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and services.
      </p>
    </section>

    <section class="section">
      <h2 class="heading">2. Information We Collect</h2>

      <h3 class="sub-heading">2.1 Personal Information</h3>
      <p class="paragraph">
        We collect information that you provide directly to us, including:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">Email address and phone number</li>
        <li class="bullet-point">First name and last name</li>
        <li class="bullet-point">Age and gender</li>
        <li class="bullet-point">Location data (address, city, state, country, coordinates)</li>
        <li class="bullet-point">Profile photos (up to 3 images stored securely)</li>
        <li class="bullet-point">Bio text and voice recordings</li>
        <li class="bullet-point">Subscription and payment information</li>
      </ul>

      <h3 class="sub-heading">2.2 Authentication Information</h3>
      <p class="paragraph">
        We support multiple authentication methods:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">Email and password authentication</li>
        <li class="bullet-point">Google OAuth (Google ID)</li>
        <li class="bullet-point">Apple Sign In (Apple ID)</li>
      </ul>

      <h3 class="sub-heading">2.3 Usage Data</h3>
      <p class="paragraph">
        We track your daily usage including:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">Number of swipes, pops (likes), and super likes used</li>
        <li class="bullet-point">Rewinds used</li>
        <li class="bullet-point">Matches and conversations</li>
        <li class="bullet-point">Lobby participation</li>
      </ul>

      <h3 class="sub-heading">2.4 Device Information</h3>
      <p class="paragraph">
        We collect device identifiers and push notification tokens (FCM tokens) to send you notifications about matches, messages, and other app activities.
      </p>
    </section>

    <section class="section">
      <h2 class="heading">3. How We Use Your Information</h2>
      <p class="paragraph">
        We use the information we collect to:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">Create and manage your account</li>
        <li class="bullet-point">Match you with other users based on preferences</li>
        <li class="bullet-point">Facilitate communication between matched users</li>
        <li class="bullet-point">Enable group dating lobbies and events</li>
        <li class="bullet-point">Send push notifications about matches, messages, super likes, and profile views</li>
        <li class="bullet-point">Process subscription payments and manage premium features</li>
        <li class="bullet-point">Verify your identity through phone and email verification</li>
        <li class="bullet-point">Provide customer support and respond to inquiries</li>
        <li class="bullet-point">Improve our services and develop new features</li>
        <li class="bullet-point">Ensure platform safety and prevent fraud</li>
      </ul>
    </section>

    <section class="section">
      <h2 class="heading">4. Data Storage and Security</h2>

      <h3 class="sub-heading">4.1 Storage</h3>
      <p class="paragraph">
        Your data is stored securely:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">Profile information, matches, and messages are stored in MongoDB databases</li>
        <li class="bullet-point">Photos and voice recordings are stored in Amazon S3 with secure signed URLs</li>
        <li class="bullet-point">All data is encrypted in transit and at rest</li>
      </ul>

      <h3 class="sub-heading">4.2 Security Measures</h3>
      <p class="paragraph">
        We implement industry-standard security measures including:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">Secure authentication and authorization</li>
        <li class="bullet-point">Encrypted data transmission (HTTPS/TLS)</li>
        <li class="bullet-point">Regular security audits and updates</li>
        <li class="bullet-point">Access controls and monitoring</li>
      </ul>
    </section>

    <section class="section">
      <h2 class="heading">5. Third-Party Services</h2>
      <p class="paragraph">
        We use the following third-party services that may collect information:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">Google OAuth: For Google Sign-In authentication</li>
        <li class="bullet-point">Apple Sign In: For Apple authentication</li>
        <li class="bullet-point">Firebase Cloud Messaging (FCM): For push notifications</li>
        <li class="bullet-point">Stripe: For payment processing and subscription management</li>
        <li class="bullet-point">Amazon S3: For secure file storage</li>
      </ul>
      <p class="paragraph">
        These services have their own privacy policies governing the use of your information.
      </p>
    </section>

    <section class="section">
      <h2 class="heading">6. Your Rights and Choices</h2>
      <p class="paragraph">
        You have the right to:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">Access your personal information</li>
        <li class="bullet-point">Update or correct your profile information</li>
        <li class="bullet-point">Delete your account (soft delete - account deactivation)</li>
        <li class="bullet-point">Control notification preferences</li>
        <li class="bullet-point">Opt out of certain data collection where applicable</li>
      </ul>
      <p class="paragraph">
        You can manage these settings in the app or contact us for assistance.
      </p>
    </section>

    <section class="section">
      <h2 class="heading">7. Data Retention</h2>
      <p class="paragraph">
        We retain your information for as long as your account is active or as needed to provide services. When you delete your account, we will deactivate it and mark it with a deletion timestamp. Some information may be retained for legal or legitimate business purposes.
      </p>
    </section>

    <section class="section">
      <h2 class="heading">8. Children's Privacy</h2>
      <p class="paragraph">
        DropAddy is intended for users who are 18 years of age or older. We do not knowingly collect personal information from children under 18. If we become aware that we have collected information from a child under 18, we will take steps to delete such information.
      </p>
    </section>

    <section class="section">
      <h2 class="heading">9. Changes to This Privacy Policy</h2>
      <p class="paragraph">
        We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
      </p>
    </section>

    <section class="section">
      <h2 class="heading">10. Contact Us</h2>
      <p class="paragraph">
        If you have any questions about this Privacy Policy or our data practices, please contact us through the Help & Account Settings section in the app or via email at support@poptheballoon.com.
      </p>
    </section>
  </div>
</div>
